<template>
  <div :class="$style.activitySummaryContainer">
    <header
      :class="$style.activitySummaryHeader"
      class="relative rounded rounded-lg">
      <div class="relative flex flex-col items-start gap-4 p-8">
        <div class="flex flex-row flex-wrap gap-2">
          <span
            v-for="disciplineName in disciplineNames"
            :key="disciplineName"
            class="bg-brand text-brand-text rounded rounded-md py-2 px-3">
            {{ capitalize($t(`disciplines.${disciplineName}`)) }}
          </span>
        </div>
        <h1 class="my-2 text-light max-w-md">
          {{ activityName }}
        </h1>
      </div>
      <img
        :src="headerUrl"
        class="absolute">
    </header>
    <div
      :class="$style.activitySummaryBody"
      class="pt-8">
      <main :class="$style.activitySummaryMain">
        <span
          v-if="activity.description"
          class="font-bold">
          {{ $t('activities.event_description') }}
        </span>
        <div
          :class="$style.activitySummaryDescription"
          v-html="activity.description"/>
        <table
          v-if="hasFiles"
          :class="$style.activitySummaryTable"
          class="">
          <thead>
            <tr>
              <th class="w-full">{{ $t('activities.file_name') }}</th>
              <th>{{ $t('activities.file_size') }}</th>
              <th>{{ $t('activities.file_date') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="file in activity.meta.custom_files"
              :key="file.id"
              class="hover:bg-background-dark cursor-pointer">
              <td class="p-4">
                <a
                  :href="file.download_url"
                  target="_blank"
                  class="no-underline text-text-dark block w-full h-full">
                  <span>{{ parseFileName(file.name) }}</span>
                </a>
              </td>
              <td class="p-4">
                <a
                  :href="file.download_url"
                  target="_blank"
                  class="no-underline text-text-dark block w-full h-full">
                  {{ file.size_human_readable }}
                </a>
              </td>
              <td class="p-4">
                <a
                  :href="file.download_url"
                  target="_blank"
                  class="no-underline text-text-dark block w-full h-full">
                  {{ parseFileDate(file.created_at) }}
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </main>
      <aside :class="$style.activitySummaryAside">
        <div v-if="hasDate">
          <span class="font-bold">{{ $t('activities.date_and_time') }}</span>
          <p>{{ parsedDate }}</p>
        </div>
        <div v-if="hasPlace">
          <span class="font-bold">{{ $t('activities.location') }}</span>
          <p>{{ parsedPlace }}</p>
        </div>
        <img
          v-if="bannerUrl"
          :src="bannerUrl"
          class="w-full rounded rounded-lg mt-6 border border-background-dark">
      </aside>
    </div>
  </div>
</template>

<script>
import { ActivityModel } from '@/src/server/modules/activities/ActivityModel'
import { DateTime } from 'luxon'
import { capitalize } from '@/src/server/helpers/string'

export default {
  data: () => ({
    activity: {
      required: true,
      type: ActivityModel,
    },
  }),
  computed: {
    activityId () {
      return this.$route.query.id
    },
    headerUrl () {
      if (this.activity.meta && this.activity.meta.header_banner_url) return this.activity.meta.header_banner_url
      else return '/static/manager-header.jpg'
    },
    bannerUrl () {
      if (this.activity.meta && this.activity.meta.poster_url) return this.activity.meta.poster_url
      else return false
    },
    hasDate () {
      if (this.activity.sourceType === 'competition') {
        return this.activity.meta.competition_data.start_date || this.activity.meta.competition_data.end_date
      } else return this.activity.startDate || this.activity.endDate
    },
    parsedDate () {
      let start = null
      let end = null

      if (this.activity.sourceType === 'competition') {
        start = this.activity.meta.competition_data.start_date
        end = this.activity.meta.competition_data.end_date
      } else {
        start = this.activity.startDate
        end = this.activity.endDate
      }

      if (start && end) {
        return this.$t('activities.date_from_to', {
          from: start.toLocaleString(DateTime.DATE_SHORT),
          to: end.toLocaleString(DateTime.DATE_SHORT),
        })
      }

      if (start) {
        return this.$t('activities.date_from', {
          from: start.toLocaleString(DateTime.DATE_SHORT),
        })
      }

      if (end) {
        return this.$t('activities.date_to', {
          to: end.toLocaleString(DateTime.DATE_SHORT),
        })
      }
    },
    hasPlace () {
      if (this.activity.sourceType === 'competition') {
        return this.activity.meta.competition_data.venue_name !== undefined
      } else return this.activity.place || this.activity.country
    },
    parsedPlace () {
      let country

      if (this.activity.country) {
        country = this.activity.country.toUpperCase()
      }

      let place = null

      if (this.activity.sourceType === 'competition') {
        place = this.activity.meta.competition_data.venue_name
      } else place = this.activity.place

      return [place, country]
        .filter(magic => magic)
        .join(', ')
    },
    disciplineNames () {
      if (this.activity.sourceType === 'competition' && this.activity.meta) {
        return [this.activity.meta.competition_data.discipline_name]
      } else if (this.activity.meta) return this.activity.meta.discipline_names
      else return []
    },
    activityName () {
      if (this.activity.sourceType === 'competition') {
        return this.activity.meta.competition_data.name
      } else return this.activity.name
    },
    hasFiles () {
      if (this.activity.meta) {
        return this.activity.meta.custom_files && this.activity.meta.custom_files.length > 0
      } else return false
    },
  },
  async created () {
    this.activity = await this.$activitiesService.fetchActivity(this.activityId)
  },
  methods: {
    capitalize (string) {
      return capitalize(string)
    },
    parseFileDate (date) {
      return date.toLocaleString(DateTime.DATE_SHORT)
    },
    parseFileName (name) {
      let maxlength = 50

      return (name.length > maxlength) ? name.slice(0, maxlength - 1) + '…' : name
    },
  },
}
</script>

<style lang="scss" module>
.activity-summary {

  &__container {
    @apply w-full;
  }

  &__header {
    background-color: grey;
    overflow: hidden;
    div {
        z-index: 1;
        background: linear-gradient(
            to bottom,
            transparent,
            var(--dark-darkest) 150%
        );
    }
    img {
        z-index: 0;
        bottom: 0;
        object-fit: cover;
        object-position: left;
        height: 100%;
        width: 100%;
    }
  }

  &__body {
    @apply flex flex-col-reverse;
    gap: 2.5rem;
    @screen md {
      @apply flex-row;
    }
  }

  &__table {
    @apply table-auto w-full mt-8 overflow-x-auto;
    display: block;
    tr {
        @apply border border-background-dark;
        td, th {
            @apply py-3 px-6;
            white-space: nowrap;
            word-break: break-all;
        }
        span {
          white-space: normal;
        }
        th {
            @apply text-sm text-text-lightest font-medium;
        }
    }
  }

  &__description {
    @apply my-4;
  }

  &__main {
    flex-basis: 66.6%;
  }

  &__aside {
    flex-basis: 33.3%;
    min-width: 200px;
  }
}
</style>



// WEBPACK FOOTER //
// src/server/components/Activities/Widgets/ActivitySummary.vue